import React, { useState, useEffect } from "react";
import { useSignIn } from "@clerk/react";
import { useSignIn as useLegacySignIn } from "@clerk/react/legacy";
import { useHistory, useLocation } from "react-router-dom";
import { Col, Row, Container, Form, Button, Alert } from "@themesberg/react-bootstrap";
import GoLocalBrandMark from "../../components/GoLocalBrandMark";
import PhoneNumberInput from "../../components/PhoneNumberInput";
import { Routes } from "../../routes";

import "./signin-golocal.css";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" style={{ flexShrink: 0 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.54 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
  </svg>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+[1-9]\d{6,14}$/;

export default () => {
  // Clerk v6 API: returns { signIn, errors, fetchStatus } instead of { isLoaded, signIn, setActive }
  const { signIn, fetchStatus } = useSignIn();
  // OAuth goes through the stable/legacy hook: Clerk's backend rejects the
  // OAuth strategy param when sent via the experimental signIn.sso() call.
  const { signIn: legacySignIn, isLoaded: legacySignInLoaded } = useLegacySignIn();
  const history = useHistory();
  const location = useLocation();
  const [step, setStep] = useState("identifier"); // "identifier" | "code"
  const [channel, setChannel] = useState("email"); // "email" | "phone"
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Set when signIn.status comes back "needs_client_trust" (new device):
  // the next code submission must go through signIn.mfa.* instead of
  // signIn.emailCode/phoneCode.*. See verifyCode() below.
  const [awaitingTrust, setAwaitingTrust] = useState(false);
  const [trustChannel, setTrustChannel] = useState(null); // "email" | "phone" | null

  useEffect(() => {
    if (new URLSearchParams(location.search).get("denied") === "1") {
      setError("Your account doesn't have admin access.");
    }
  }, [location.search]);

  const sendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { error: sendError } =
        channel === "email"
          ? await signIn.emailCode.sendCode({ emailAddress: identifier })
          : await signIn.phoneCode.sendCode({ phoneNumber: identifier });

      if (sendError) {
        setError(sendError.longMessage || sendError.message || "Couldn't find your account.");
        return;
      }

      setStep("code");
    } catch (err) {
      setError(err.errors?.[0]?.longMessage || err.errors?.[0]?.message || err.message || "Couldn't find your account.");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      // Note: signIn.emailCode/phoneCode/mfa .verifyCode() calls only ever
      // resolve to { error }, per @clerk/shared's SignInFutureResource type
      // (node_modules/@clerk/shared/dist/types/signInFuture.d.ts). The
      // current status lives on the reactive `signIn` resource itself, not
      // on the resolved value, so that's what we read below.
      const { error: verifyError } = awaitingTrust
        ? trustChannel === "email"
          ? await signIn.mfa.verifyEmailCode({ code })
          : await signIn.mfa.verifyPhoneCode({ code })
        : channel === "email"
        ? await signIn.emailCode.verifyCode({ code })
        : await signIn.phoneCode.verifyCode({ code });

      if (verifyError) {
        setError(verifyError.longMessage || verifyError.message || "Invalid code.");
        return;
      }

      // New device: first factor is verified but Clerk requires an
      // additional second-factor verification to establish Client Trust
      // before the sign-in can complete.
      // https://clerk.com/docs/guides/development/custom-flows/authentication/client-trust
      if (signIn.status === "needs_client_trust") {
        const wantsEmail = signIn.supportedSecondFactors.some((f) => f.strategy === "email_code");
        const wantsPhone = signIn.supportedSecondFactors.some((f) => f.strategy === "phone_code");

        if (!wantsEmail && !wantsPhone) {
          setError("This device isn't trusted yet, and no supported verification method is available. Contact an admin.");
          return;
        }

        const { error: mfaSendError } = wantsEmail
          ? await signIn.mfa.sendEmailCode()
          : await signIn.mfa.sendPhoneCode();

        if (mfaSendError) {
          setError(mfaSendError.longMessage || mfaSendError.message || "Couldn't send the additional verification code.");
          return;
        }

        setTrustChannel(wantsEmail ? "email" : "phone");
        setAwaitingTrust(true);
        setCode("");
        setError("This is a new device — enter the additional verification code we just sent.");
        return;
      }

      if (signIn.status !== "complete") {
        setError(`Couldn't complete sign-in (status: ${signIn.status}). Please try again or contact an admin.`);
        return;
      }

      const { error: finalizeError } = await signIn.finalize();

      if (finalizeError) {
        setError(finalizeError.longMessage || finalizeError.message || "Sign-in could not be completed.");
        return;
      }

      if (signIn.status !== "complete" || !signIn.createdSessionId) {
        setError("Sign-in could not be completed. Please try again.");
        return;
      }

      history.push(Routes.Users.path);
    } catch (err) {
      setError(err.errors?.[0]?.longMessage || err.errors?.[0]?.message || err.message || "Invalid code.");
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setError("");
    try {
      const { error: sendError } = awaitingTrust
        ? trustChannel === "email"
          ? await signIn.mfa.sendEmailCode()
          : await signIn.mfa.sendPhoneCode()
        : channel === "email"
        ? await signIn.emailCode.sendCode({ emailAddress: identifier })
        : await signIn.phoneCode.sendCode({ phoneNumber: identifier });

      if (sendError) {
        setError(sendError.longMessage || sendError.message || "Failed to resend code.");
      }
    } catch (err) {
      setError(err.errors?.[0]?.longMessage || err.errors?.[0]?.message || err.message || "Failed to resend code.");
    }
  };

  const handleSocialSignIn = async (strategy) => {
    setError("");
    try {
      // redirectUrl         = OAuth callback handler (where AuthenticateWithRedirectCallback is mounted)
      // redirectUrlComplete = final destination after the OAuth flow completes
      await legacySignIn.authenticateWithRedirect({
        strategy,
        redirectUrl: `${window.location.origin}/sso-callback`,
        redirectUrlComplete: `${window.location.origin}/#${Routes.Users.path}`,
      });
    } catch (err) {
      setError(err.errors?.[0]?.longMessage || err.errors?.[0]?.message || err.message || "Social sign-in failed.");
    }
  };

  const isBusy = loading || fetchStatus === "fetching";
  const isSocialBusy = isBusy || !legacySignInLoaded;
  const canSendCode = channel === "email" ? EMAIL_RE.test(identifier) : PHONE_RE.test(identifier);

  return (
    <main className="golocal-signin-page">
      <div className="golocal-signin-bg" aria-hidden />
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} sm={10} md={8} lg={6} xl={5}>
            <div className="golocal-signin-card">
              <div className="text-center mb-4">
                <GoLocalBrandMark />
              </div>
              <h1 className="golocal-signin-title text-center">Admin Sign In</h1>
              <p className="golocal-signin-lead text-center">GoLocal staff portal</p>

              {error && (
                <Alert variant="danger" className="py-2 mb-3" style={{ fontSize: "0.9rem" }}>
                  {error}
                </Alert>
              )}

              {step === "identifier" && (
                <>
                  <div className="d-flex gap-2 mb-3">
                    <button
                      type="button"
                      className="golocal-social-btn flex-fill"
                      onClick={() => handleSocialSignIn("oauth_google")}
                      disabled={isSocialBusy}
                    >
                      <GoogleIcon />
                      <span>Google</span>
                    </button>
                    <button
                      type="button"
                      className="golocal-social-btn flex-fill"
                      onClick={() => handleSocialSignIn("oauth_apple")}
                      disabled={isSocialBusy}
                    >
                      <AppleIcon />
                      <span>Apple</span>
                    </button>
                  </div>

                  <div className="golocal-divider mb-3">
                    <span>or continue with a code</span>
                  </div>

                  <Form onSubmit={sendCode}>
                    <div className="golocal-channel-toggle mb-3">
                      <button
                        type="button"
                        className={`golocal-channel-btn${channel === "email" ? " active" : ""}`}
                        onClick={() => {
                          setChannel("email");
                          setIdentifier("");
                        }}
                      >
                        Email
                      </button>
                      <button
                        type="button"
                        className={`golocal-channel-btn${channel === "phone" ? " active" : ""}`}
                        onClick={() => {
                          setChannel("phone");
                          setIdentifier("");
                        }}
                      >
                        Phone
                      </button>
                    </div>

                    <Form.Group className="mb-4">
                      <Form.Label>{channel === "email" ? "Email address" : "Phone number"}</Form.Label>
                      {channel === "email" ? (
                        <Form.Control
                          type="email"
                          value={identifier}
                          onChange={(e) => setIdentifier(e.target.value)}
                          placeholder="you@example.com"
                          required
                          autoFocus
                          autoComplete="email"
                        />
                      ) : (
                        <PhoneNumberInput value={identifier} onChange={setIdentifier} autoFocus />
                      )}
                    </Form.Group>

                    <div className="d-grid">
                      <Button
                        type="submit"
                        className="golocal-btn-signin"
                        disabled={isBusy || !canSendCode}
                      >
                        {loading ? "Sending code…" : "Continue"}
                      </Button>
                    </div>
                  </Form>
                </>
              )}

              {step === "code" && (
                <Form onSubmit={verifyCode}>
                  <p className="golocal-signin-lead text-center" style={{ marginBottom: "1.25rem" }}>
                    Enter the 6-digit code sent to {identifier}
                  </p>

                  <Form.Group className="mb-4">
                    <Form.Label>Verification code</Form.Label>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="123456"
                      maxLength={6}
                      required
                      autoFocus
                      autoComplete="one-time-code"
                    />
                  </Form.Group>

                  <div className="d-grid mb-3">
                    <Button
                      type="submit"
                      className="golocal-btn-signin"
                      disabled={isBusy || code.length !== 6}
                    >
                      {loading ? "Verifying…" : "Verify"}
                    </Button>
                  </div>

                  <div className="d-flex justify-content-between">
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      style={{ fontSize: "0.875rem" }}
                      onClick={() => {
                        setStep("identifier");
                        setCode("");
                        setError("");
                      }}
                    >
                      Use a different {channel === "email" ? "email" : "number"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      style={{ fontSize: "0.875rem" }}
                      onClick={resendCode}
                      disabled={isBusy}
                    >
                      Resend code
                    </button>
                  </div>
                </Form>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </main>
  );
};
